import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {Helper} from '../../helpers/helper.service';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Order} from '../Models/Order';
import {ServiceRequest} from '../../dealer-system/Models/ServiceRequest';
import {map, catchError} from 'rxjs/operators';
import {CustomerDeliveryAddress} from '../Models/CustomerDeliveryAddress';

@Injectable()
export class BackOfficeOrderService {
  constructor(private helper: Helper, private http: HttpClient) {
  }

  public generateOrderTemp(order, kind) {
    return this.http.post(environment.apiUrl + '/backoffice/order/pdf?kind=' + kind, order, {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => {
        const contentDispositionHeader = resp.headers.get('content-disposition');
        const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        return {'url': window.URL.createObjectURL(new Blob([resp.body], {type: 'application/pdf'})), 'filename': result.replace(/"/g, '')};
      }),
      catchError(res => this.helper.catchError(res))
    );
  }

  public getInvoiceForAnOrder(orderId, kind) {
    return this.http.get(environment.apiUrl + '/backoffice/order/' + orderId + '/pdf?kind=' + kind, {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => {
        const contentDispositionHeader = resp.headers.get('content-disposition');
        const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        return {'url': window.URL.createObjectURL(new Blob([resp.body], {type: 'application/pdf'})), 'filename': result.replace(/"/g, '')};
      }),
      catchError(res => this.helper.catchError(res))
    );
  }

  public getBackofficeOrders(gridData = new HttpParams(), params = new HttpParams(), scope = 'backoffice') {
    if (gridData?.get?.('sortBy') === 'id') {
      gridData = gridData.set('sortBy', 'created');
    }
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/order/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  /**
   * The insured orders that still need attention: a POS option is present and the
   * registration is missing/incomplete, or it has not been exported yet (US-606 D5).
   * The whole filter is the backend's (BackOffice\PosRegistrationController, US-607); the
   * rows it returns are computed, not serialized Orders, because "POS term" and "units" are
   * derived across an order's insured lines.
   */
  public getPosInsuredOrders(gridData = new HttpParams(), params = new HttpParams()) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/pos-registration/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getOrder(orderId) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/order/' + orderId)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getPdf(orderId ,kind?: string) {
    const kindParam = kind ? `?kind=${kind}` : '';
    return this.http.get(environment.apiUrl + '/dealer-system/order/' + orderId + '/pdf' + kindParam, {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => this.helper.fileDownloadMap(resp, true)),
      catchError(res => this.helper.catchError(res))
    );
  }

  public getCustomFabricPackagingPdf(orderId) {
    return this.http.get(environment.apiUrl + '/backoffice/order/' + orderId + '/custom-fabric-packaging-pdf', {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => this.helper.fileDownloadMap(resp)),
      catchError(res => this.helper.catchError(res))
    );
  }

  public sendPdf(orderId, email) {
    return this.http.post(environment.apiUrl + '/backoffice/order/' + orderId, {'email': email})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageOrder(order: Order, isEdit, id = null) {
    if (isEdit) {
      return this.http.put<any>(environment.apiUrl + '/backoffice/order/' + id, order)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/', order)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageOrderInfo(date, status, id = null) {
    return this.http.put<any>(environment.apiUrl + '/backoffice/order/info/' + id, {
      expectedDeliveryWeek: date,
      status: status
    })
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageServiceRequest(serviceRequest: ServiceRequest, id) {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/' + id + '/service-request', serviceRequest)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public downloadOrder(gridData = new HttpParams()) {
    return this.http.get(environment.apiUrl + '/backoffice/order/download-orders?' + gridData.toString(), {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => {
        const contentDispositionHeader = resp.headers.get('content-disposition');
        const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        return {'url': window.URL.createObjectURL(new Blob([resp.body], {type: 'application/pdf'})), 'filename': result.replace(/"/g, '')};
      }),
      catchError(res => this.helper.catchError(res))
    );
  }

  public bulkAction(params) {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/bulk-action', params)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public cancelOrder(orderID) {
    return this.http.put<any>(environment.apiUrl + '/backoffice/order/' + orderID + '/cancel', {id: orderID})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public addCustomRow(orderId, params, isEdit) {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/' + orderId + '/add-extra', {customRows: params})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public addDiscount(orderId, params) {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/' + orderId + '/add-extra', params)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public deleteCustomRow(orderId) {

    return this.http.delete<any>(environment.apiUrl + '/backoffice/order/' + orderId + '/add-extra', {
      body: {delete_customRows: true}
    }).pipe(catchError(res => this.helper.catchError(res)));
  }

  public deleteDiscount(orderId) {
    return this.http.delete<any>(environment.apiUrl + '/backoffice/order/' + orderId + '/add-extra', {
      body: {delete_discount: true}
    })
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public splitOrder(params) {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/split', params)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public exportOrders(gridData = new HttpParams()) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/order/export?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getOption(id: number) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/order/structured-options/' + id)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public sendDeliveryEmails() {
    return this.http.post<any>(environment.apiUrl + '/backoffice/order/send-email', {})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getReferenceOrders(gridData = new HttpParams(), scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/order/references/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getDeliveryWeeks(gridData = new HttpParams(), scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/order/delivery-weeks/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public updateCustomerDeliveryAddress(customerDeliveryAddress: CustomerDeliveryAddress, id) {
    return this.http.put<any>(environment.apiUrl + '/backoffice/order/update-customer-delivery-address/' + id, customerDeliveryAddress)
      .pipe(catchError(res => this.helper.catchError(res)));
  }
}
